/**
 * Pure checkout-outcome classification helpers (999.1333).
 *
 * CheckoutPage's confirm flow polls a workflow instance (Stripe payment) and
 * then polls subscriptions to confirm each cart item actually went through.
 * The bug: neither poll loop ever recorded a *failure* signal anywhere — a
 * synchronously-failed item (data.results[].status==='failed'), an instance
 * that transitions to 'failed' mid-poll, or poll-exhaustion (never confirmed)
 * all fell through to the unconditional success step. These helpers make
 * "did this item actually succeed" an explicit, testable value instead of
 * an implicit side effect of loop control flow.
 */

/**
 * Classifies a polled workflow instance during the paused-for-payment loop.
 * @param {object|null|undefined} instance
 * @returns {{redirectUrl: string} | 'completed' | 'failed' | 'manual_review' | null}
 *   null means "still running — keep polling".
 *   'manual_review' (999.4741/999.4758/999.4760) is its OWN explicit value —
 *   never 'completed' or 'failed'. It is terminal (999.4741: no supported
 *   transition leads out of it), but neither outcome is known: a Stripe
 *   refund/charge may already have executed and is unverified. Callers must
 *   not treat it as success or failure.
 *
 * (999.4746 PAY-WORKFLOW-STATUS-CONTRACT: this function is a registered
 * consumer, mechanically checked by AST-extracting each `instance.status ===
 * '<literal>'` comparison — an imported identifier in place of the literal
 * is invisible to that extractor, which silently defeats the ratchet
 * (a ledgered gap can never be detected as resolved). Use the literal
 * string here, even though `MANUAL_REVIEW_STATUS` exists for other callers.)
 */
export function classifyInstancePoll(instance) {
  if (!instance) return null;
  if (instance.status === 'paused_for_payment') {
    const checkoutUrl = instance.context?.collect_payment?.checkout_url;
    if (checkoutUrl) return { redirectUrl: checkoutUrl };
    return null;
  }
  if (instance.status === 'completed') return 'completed';
  if (instance.status === 'failed') return 'failed';
  // 999.4761: admin-canceled checkout (e.g. canceling a paused_for_payment
  // instance) is terminal — treat as failed so the poll loop stops instead
  // of polling forever.
  if (instance.status === 'canceled') return 'failed';
  if (instance.status === 'manual_review') return 'manual_review';
  return null;
}

/**
 * Seeds a planId -> 'completed'|'failed' map from the checkout/execute
 * response's synchronous results (e.g. free-plan activation, or a
 * synchronous rejection like "plan not found" / "unknown action type").
 * @param {Array<{planId: string, status: string}>} results
 * @returns {Object<string, 'completed'|'failed'>}
 */
export function seedOutcomes(results) {
  const outcomes = {};
  (results || []).forEach((r) => {
    if (r.status === 'failed' || r.status === 'completed') outcomes[r.planId] = r.status;
  });
  return outcomes;
}

/**
 * True if the item now has an active/trialing subscription.
 * @param {{planId: string}} item
 * @param {Array} subscriptions
 * @returns {boolean}
 */
export function isItemActive(item, subscriptions) {
  return (subscriptions || []).some((s) =>
    ['active', 'trialing'].includes(s.status) &&
    (s.plan_id === item.planId || s.planId === item.planId)
  );
}

/**
 * True once every cart item has a resolved outcome (already in `outcomes`,
 * or now has an active subscription) — signals the confirmation poll loop
 * can stop.
 * @param {Array<{planId: string}>} items
 * @param {Object<string, string>} outcomes
 * @param {Array} subscriptions
 * @returns {boolean}
 */
export function allItemsResolved(items, outcomes, subscriptions) {
  return items.every((item) => !!outcomes[item.planId] || isItemActive(item, subscriptions));
}

/**
 * Finalizes outcomes after polling ends (by resolution or by exhausting
 * attempts). Any item still unresolved is NOT active, so it can't be
 * confirmed successful — treat it as failed. The UI must never claim
 * success for an item it can't confirm.
 * @param {Array<{planId: string}>} items
 * @param {Object<string, string>} outcomes
 * @param {Array} subscriptions
 * @returns {Object<string, 'completed'|'failed'>}
 */
export function finalizeOutcomes(items, outcomes, subscriptions) {
  const finalOutcomes = { ...outcomes };
  items.forEach((item) => {
    if (finalOutcomes[item.planId]) return;
    finalOutcomes[item.planId] = isItemActive(item, subscriptions) ? 'completed' : 'failed';
  });
  return finalOutcomes;
}

/**
 * @param {Object<string, string>} outcomes
 * @returns {boolean} true if any item's outcome is 'failed'
 */
export function anyFailed(outcomes) {
  return Object.values(outcomes).some((s) => s === 'failed');
}

/**
 * 999.4741/999.4758/999.4760 BLOCK 6 (harrison/law review): 'manual_review'
 * must be excluded from the failure path (anyFailed only ever matches the
 * literal 'failed', so this is already true by construction) AND must be
 * checked for explicitly so the caller can render a "parked for review"
 * outcome instead of silently falling through to the plain success step —
 * the customer-reachable path toward treating an unverified provider action
 * as either a confirmed success or a routine failure inviting a retry.
 * @param {Object<string, string>} outcomes
 * @returns {boolean} true if any item's outcome is 'manual_review'
 */
export function anyManualReview(outcomes) {
  return Object.values(outcomes).some((s) => s === 'manual_review');
}
